/**
 * ARG Sprint 9 (B-67) — PdfSourceViewer.
 *
 * Pattern Kepler "trace each number back to its exact line item highlighted in
 * the source document". Composant split-pane qui :
 *   1. Affiche le PDF source via iframe natif (Chromium PDF viewer).
 *   2. Saute automatiquement à la `page` ciblée via fragment URL `#page=N`.
 *   3. Affiche un header de traçabilité avec la `designation` AO + page,
 *      pour que l'admin sache visuellement quelle ligne il vérifie.
 *   4. Bouton "fermer" pour replier le panneau.
 *
 * Sprint 9 V1 limites volontaires :
 *   - Pas de highlight pixel-perfect (= bbox `x/y/w/h` ignoré V1, le viewer
 *     natif iframe ne supporte pas l'overlay). UI degrade : on jump-to-page
 *     et l'admin lit la zone à l'oeil. Sprint 10+ V2 ajoutera PDF.js + canvas
 *     overlay quand un provider Vision retournera les coords pixel.
 *   - Pas de preload : l'iframe se charge à l'ouverture. Acceptable car la
 *     plupart des AO font < 50 pages.
 *
 * Best-effort partout :
 *   - Si l'iframe fail à charger (PDF supprimé R2 ?), affiche "viewer
 *     indisponible" + lien direct download.
 *   - Si `page` est null ou < 1, affiche le PDF page 1 sans jump.
 */

import { useEffect, useRef, useState } from 'react';
import { X, FileText, ExternalLink } from 'lucide-react';
import { apiHref } from '../lib/api-client';

export type PdfSourceViewerProps = {
  /** Tender ID — utilisé pour construire l'URL de download du PDF. */
  tenderId: string;
  /**
   * Page courante à afficher (1-indexed). `null` = ouvre le PDF page 1
   * sans `#page=` (= comportement par défaut du viewer natif).
   */
  page: number | null;
  /**
   * Designation AO de la ligne sélectionnée. Affichée dans le header
   * comme hint visuel ("recherche cette désignation sur la page"). Optional :
   * si `null/undefined`, le header montre juste la page.
   */
  designation?: string | null;
  /**
   * V2 (Sprint 10+) — bbox pixel pour overlay highlight. Sprint 9 V1 ignore
   * silencieusement (= prop déclarée pour stabilité d'API mais pas utilisée).
   */
  bbox?: { x?: number; y?: number; w?: number; h?: number } | null;
  /**
   * Callback quand l'admin ferme le panneau. Le caller met à jour son state
   * `showPdf=false`.
   */
  onClose: () => void;
};

/**
 * Tronque la designation à 80 chars + ellipse pour le header. Évite que le
 * panneau header occupe 3 lignes sur les designations très longues.
 */
function truncateDesignation(text: string, max = 80): string {
  if (text.length <= max) return text;
  return text.substring(0, max - 1) + '…';
}

export function PdfSourceViewer({
  tenderId,
  page,
  designation,
  bbox: _bbox, // Sprint 9 V1 : ignoré explicitement, V2 quand pixel-aware.
  onClose,
}: PdfSourceViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Quand `page` change, reload l'iframe avec le fragment #page=N.
  // Pattern strictement aligné avec l'ancien inline code dans TenderDetail
  // (= rétrocompat comportement Sprint 6 / B-30).
  useEffect(() => {
    if (!iframeRef.current) return;
    setLoadError(false);
    const base = apiHref(`/api/tenders/${tenderId}/download?kind=pdf`);
    const url = page != null && page >= 1 ? `${base}#page=${page}` : base;
    iframeRef.current.src = url;
  }, [page, tenderId]);

  const downloadHref = apiHref(`/api/tenders/${tenderId}/download?kind=pdf`);

  return (
    <div
      className="sticky top-4 self-start flex flex-col gap-2"
      style={{ height: 'calc(100vh - 8rem)' }}
      data-testid="pdf-source-viewer"
    >
      {/* Header de traçabilité : page + designation (hint visuel). */}
      <div className="flex items-start justify-between gap-2 px-3 py-2 bg-ink-800/80 border border-ink-700 rounded text-xs">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <FileText className="h-4 w-4 text-brand-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-mono text-brand-300">
              {page != null ? `Page ${page}` : 'PDF source'}
            </div>
            {designation && (
              <div
                className="text-steel-400 truncate mt-0.5"
                title={designation}
                data-testid="pdf-viewer-designation-hint"
              >
                {truncateDesignation(designation)}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <a
            href={downloadHref}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 text-steel-400 hover:text-brand-300 rounded"
            title="Ouvrir dans un nouvel onglet"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <button
            onClick={onClose}
            className="p-1 text-steel-400 hover:text-red-400 rounded"
            title="Fermer le panneau PDF"
            aria-label="Fermer le panneau PDF"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Viewer iframe. Best-effort : si onError, fallback message. */}
      {loadError ? (
        <div className="flex-1 flex items-center justify-center border border-ink-700 rounded bg-ink-900/40 text-steel-400 text-sm p-6 text-center">
          <div>
            <div className="text-amber-400 font-medium mb-2">Viewer PDF indisponible</div>
            <div className="text-2xs mb-3">
              Le PDF n'a pas pu être chargé dans ce panneau (peut-être supprimé du
              storage R2). Vous pouvez l'ouvrir directement :
            </div>
            <a
              href={downloadHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-brand-400 hover:text-brand-300 underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Ouvrir le PDF
            </a>
          </div>
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          title="PDF source"
          src={apiHref(`/api/tenders/${tenderId}/download?kind=pdf`)}
          className="w-full flex-1 border border-ink-700 rounded bg-white"
          onError={() => setLoadError(true)}
        />
      )}
    </div>
  );
}
